import {
  DailyEventObjectParticipant,
  DailyEventObjectWaitingParticipant,
} from '@daily-co/daily-js';
import { useDailyEvent, useWaitingParticipants } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { DENY_TIMEOUT_MS } from '@web-app/constants/timeouts';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { isDialOutParticipant } from '@web-app/utils/daily-client/dial-out-participant';
import { useCallback, useEffect, useRef } from 'react';

interface UseWaitingRoomLoggingProps {
  roomId: string;
  healthieAppointmentId?: number;
  providerId?: number;
  clientId?: number;
  userId?: string | null;
}

interface WaitingParticipantInfo {
  id: string;
  name: string;
  addedAt: number;
  admitted: boolean;
}

/**
 * Hook for logging waiting room events in Daily Prebuilt UI (v2).
 *
 * Since Daily Prebuilt handles the "Admit" and "Deny" buttons internally,
 * we correlate events to determine user actions:
 *
 * Event order is unpredictable, so we handle both scenarios:
 * - waiting-participant-removed → participant-joined
 * - participant-joined → waiting-participant-removed
 *
 * Events logged:
 * - "Patient requesting to join" - participant appears in waiting room
 * - "Provider admitted waiting participant" - patient joins after being in waiting room
 * - "Provider denied waiting participant" - patient removed without joining
 */
export const useWaitingRoomLogging = ({
  roomId,
  healthieAppointmentId,
  providerId,
  clientId,
  userId,
}: UseWaitingRoomLoggingProps): void => {
  const waitingParticipantsMap = useRef<Map<string, WaitingParticipantInfo>>(
    new Map(),
  );
  const pendingDenyTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const getBaseAttrs = useCallback(
    () => ({
      roomId,
      participantType: 'provider' as const,
      healthieAppointmentId,
      providerId,
      clientId,
      timestamp: new Date().toISOString(),
    }),
    [roomId, healthieAppointmentId, providerId, clientId],
  );

  const logPatientRequestingToJoin = useCallback(
    (participantId: string, participantName: string) => {
      datadogLogs.logger.info(
        'Patient requesting to join, modal appeared',
        callLogContext('waiting-room', {
          ...getBaseAttrs(),
          participantId,
          participantName,
        }),
      );
    },
    [getBaseAttrs],
  );

  const logProviderAdmitted = useCallback(
    (participantId: string, sessionId?: string, userName?: string) => {
      datadogLogs.logger.info(
        'Provider admitted waiting participant',
        callLogContext('waiting-room', {
          ...getBaseAttrs(),
          participantId,
          participantSessionId: sessionId,
          participantName: userName || 'Unknown',
          action: 'admit',
          userId,
          healthieUserId: providerId,
        }),
      );
    },
    [getBaseAttrs, userId, providerId],
  );

  const logProviderDenied = useCallback(
    (participantId: string, participantName: string) => {
      datadogLogs.logger.info(
        'Provider denied waiting participant',
        callLogContext('waiting-room', {
          ...getBaseAttrs(),
          participantId,
          participantName,
          action: 'deny',
          userId,
          healthieUserId: providerId,
        }),
      );
    },
    [getBaseAttrs, userId, providerId],
  );

  const trackAndLogWaitingParticipant = useCallback(
    (id: string, name: string) => {
      if (waitingParticipantsMap.current.has(id)) return;

      const participantName = name || 'Unknown';
      waitingParticipantsMap.current.set(id, {
        id,
        name: participantName,
        addedAt: Date.now(),
        admitted: false,
      });

      logPatientRequestingToJoin(id, participantName);
    },
    [logPatientRequestingToJoin],
  );

  const handleWaitingParticipantAdded = useCallback(
    (ev: DailyEventObjectWaitingParticipant) => {
      const { id, name } = ev.participant;
      trackAndLogWaitingParticipant(id, name);
    },
    [trackAndLogWaitingParticipant],
  );

  const handleWaitingParticipantRemoved = useCallback(
    (ev: DailyEventObjectWaitingParticipant) => {
      const { id } = ev.participant;
      const waitingInfo = waitingParticipantsMap.current.get(id);

      // Already admitted via participant-joined - just clean up
      if (waitingInfo?.admitted) {
        waitingParticipantsMap.current.delete(id);
        return;
      }

      // Set timeout to determine DENY (in case participant-joined comes later)
      const timeoutId = setTimeout(() => {
        const info = waitingParticipantsMap.current.get(id);

        if (info && !info.admitted) {
          logProviderDenied(info.id, info.name);
        }

        waitingParticipantsMap.current.delete(id);
        pendingDenyTimeouts.current.delete(id);
      }, DENY_TIMEOUT_MS);

      pendingDenyTimeouts.current.set(id, timeoutId);
    },
    [logProviderDenied],
  );

  const { waitingParticipants } = useWaitingParticipants({
    onWaitingParticipantAdded: handleWaitingParticipantAdded,
    onWaitingParticipantRemoved: handleWaitingParticipantRemoved,
  });

  // Log participants who were already in waiting room when provider joined.
  // The onWaitingParticipantAdded callback only fires for new arrivals,
  // not for participants already waiting before the provider connected.
  useEffect(() => {
    waitingParticipants.forEach(({ id, name }) => {
      trackAndLogWaitingParticipant(id, name);
    });
  }, [waitingParticipants, trackAndLogWaitingParticipant]);

  const handleParticipantJoined = useCallback(
    (event: DailyEventObjectParticipant | null) => {
      if (!event?.participant) return;

      const {
        session_id: sessionId,
        user_name: userName,
        local,
        owner,
      } = event.participant;

      // Skip local participant (provider) or owner
      if (local || owner || isDialOutParticipant(event.participant)) return;

      // Find first non-admitted waiting participant
      const entries = Array.from(waitingParticipantsMap.current.entries());
      const matchedEntry = entries.find(([, info]) => !info.admitted);

      if (!matchedEntry) return;

      const [matchedId, matchedInfo] = matchedEntry;

      // Mark as admitted
      matchedInfo.admitted = true;

      // Clear pending deny timeout
      const pendingTimeout = pendingDenyTimeouts.current.get(matchedId);
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
        pendingDenyTimeouts.current.delete(matchedId);
      }

      logProviderAdmitted(matchedInfo.id, sessionId, userName);

      waitingParticipantsMap.current.delete(matchedId);
    },
    [logProviderAdmitted],
  );

  useDailyEvent('participant-joined', handleParticipantJoined);

  useEffect(() => {
    const timeoutsRef = pendingDenyTimeouts.current;
    const participantsRef = waitingParticipantsMap.current;

    return () => {
      timeoutsRef.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutsRef.clear();
      participantsRef.clear();
    };
  }, []);
};
